// Frozen reports. A snapshot keeps the exact inputs and results, in a canonical form, so it can be reproduced
// byte for byte and compared with a later version.
import { D } from "../money.ts";
import { analyze, ENGINE_VERSION, type Analysis } from "./analyze.ts";
import type { ModuleResult } from "./issues.ts";
import type { ArchivedRecord, Id, YearFile } from "./types.ts";

export interface Snapshot {
  kind: "ftr.report-snapshot";
  format: 1;
  engineVersion: string;
  rulesVersion: string;
  /** Supplied by the caller; tax-core never reads the clock. */
  createdAt: string;
  input: YearFile;
  analysis: Analysis;
}

/** Normalize a year file: records sorted by id, money in canonical decimal text. Meaning is unchanged. */
export function canonicalYearFile(yf: YearFile): YearFile {
  const sorted = <T extends { id: Id }>(list: T[]) => [...list].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const history = [...yf.history].sort((a: ArchivedRecord, b: ArchivedRecord) =>
    a.collection !== b.collection ? (a.collection < b.collection ? -1 : 1) : a.record.id !== b.record.id ? (a.record.id < b.record.id ? -1 : 1) : a.record.revision - b.record.revision,
  );
  return normalizeAmounts({
    ...yf,
    people: sorted(yf.people),
    firms: sorted(yf.firms),
    accounts: sorted(yf.accounts),
    sources: sorted(yf.sources),
    events: sorted(yf.events),
    links: sorted(yf.links),
    corrections: sorted(yf.corrections),
    decisions: sorted(yf.decisions),
    history,
  });
}

function normalizeAmounts<T>(v: T): T {
  if (Array.isArray(v)) return v.map(normalizeAmounts) as T;
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    if (o.state === "value" && typeof o.value === "string") return { ...o, value: new D(o.value).toFixed() } as T;
    return Object.fromEntries(Object.entries(o).map(([k, x]) => [k, normalizeAmounts(x)])) as T;
  }
  return v;
}

/** JSON with object keys sorted at every level. Array order is kept; inputs are sorted by canonicalYearFile. */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(value, (_k, v) => {
    if (v && typeof v === "object" && !Array.isArray(v)) return Object.fromEntries(Object.keys(v).sort().map((k) => [k, (v as Record<string, unknown>)[k]]));
    return v;
  });
}

export function freeze(yf: YearFile, opts: { createdAt: string }): { snapshot: Snapshot; canonical: string } {
  const input = canonicalYearFile(yf);
  const analysis = analyze(input);
  const snapshot: Snapshot = {
    kind: "ftr.report-snapshot",
    format: 1,
    engineVersion: analysis.engineVersion,
    rulesVersion: analysis.rulesVersion,
    createdAt: opts.createdAt,
    input,
    analysis,
  };
  return { snapshot, canonical: canonicalJson(snapshot) };
}

/** Re-run a snapshot's inputs and check the stored results still come out the same. */
export function verifySnapshot(s: Snapshot): { reproducible: boolean; reason?: string } {
  if (s.engineVersion !== ENGINE_VERSION) return { reproducible: false, reason: `Made with ${s.engineVersion}; this is ${ENGINE_VERSION}.` };
  const again = analyze(canonicalYearFile(s.input));
  return canonicalJson(again) === canonicalJson(s.analysis) ? { reproducible: true } : { reproducible: false, reason: "The results differ when recomputed." };
}

export interface FigureChange {
  key: string;
  label: string;
  before: string | null;
  after: string | null;
  change: string | null;
}

export interface SnapshotDiff {
  figures: FigureChange[];
  states: { key: string; label: string; before: string; after: string }[];
  sources: { added: Id[]; removed: Id[]; revised: Id[] };
  /** Forms that a correction newly replaced. */
  replaced: Id[];
}

type Figure = { label: string; value: string };

function moduleFigures<T>(prefix: string, label: string, r: ModuleResult<T> | undefined, pick: (v: T) => Record<string, Figure>): Record<string, Figure> {
  if (!r || r.state === "not_applicable") return {};
  const v = r.state === "blocked" ? r.partial : r.value;
  if (!v) return {};
  return Object.fromEntries(Object.entries(pick(v)).map(([k, f]) => [`${prefix}.${k}`, { label: `${label}: ${f.label}`, value: f.value }]));
}

function figures(s: Snapshot): Record<string, Figure> {
  const a = s.analysis;
  const people = new Map(s.input.people.map((p) => [p.id, p.label]));
  const out: Record<string, Figure> = {
    ...moduleFigures("section1256", "Futures", a.modules.section1256, (v) => ({
      total: { label: "net Section 1256 gain or loss", value: v.total },
      shortTerm: { label: "short-term (40%)", value: v.shortTerm },
      longTerm: { label: "long-term (60%)", value: v.longTerm },
      ...Object.fromEntries(v.perAccount.map((x) => [`account.${x.accountId}`, { label: x.label, value: x.basis }])),
    })),
  };
  for (const [id, r] of Object.entries(a.modules.propBusiness))
    Object.assign(
      out,
      moduleFigures(`prop.${id}`, `${people.get(id) ?? id} prop`, r, (v) => ({
        grossIncome: { label: "gross receipts", value: v.grossIncome },
        otherIncome: { label: "other income", value: v.otherIncome },
        currentExpenses: { label: "expenses deducted", value: v.currentExpenses },
        netProfit: { label: "net profit", value: v.netProfit },
      })),
    );
  for (const [id, r] of Object.entries(a.modules.selfEmployment))
    Object.assign(
      out,
      moduleFigures(`se.${id}`, `${people.get(id) ?? id} SE tax`, r, (v) => ({
        seTax: { label: "self-employment tax", value: v.seTax },
        deductibleHalf: { label: "deductible half", value: v.deductibleHalf },
      })),
    );
  return out;
}

function states(s: Snapshot): Record<string, { label: string; state: string }> {
  const people = new Map(s.input.people.map((p) => [p.id, p.label]));
  const out: Record<string, { label: string; state: string }> = { section1256: { label: "Futures", state: s.analysis.modules.section1256.state } };
  for (const [id, r] of Object.entries(s.analysis.modules.propBusiness)) out[`prop.${id}`] = { label: `${people.get(id) ?? id} prop`, state: r.state };
  for (const [id, r] of Object.entries(s.analysis.modules.selfEmployment)) out[`se.${id}`] = { label: `${people.get(id) ?? id} SE tax`, state: r.state };
  return out;
}

export function diffSnapshots(before: Snapshot, after: Snapshot, opts: { allowAcrossVersions?: boolean } = {}): SnapshotDiff {
  if (!opts.allowAcrossVersions && (before.engineVersion !== after.engineVersion || before.rulesVersion !== after.rulesVersion))
    throw new Error("These reports were made with different engine or rules versions; compare them only after re-running the older one.");
  const fa = figures(before), fb = figures(after);
  const figuresOut: FigureChange[] = [];
  for (const key of [...new Set([...Object.keys(fa), ...Object.keys(fb)])].sort()) {
    const x = fa[key]?.value ?? null, y = fb[key]?.value ?? null;
    if (x === y) continue;
    figuresOut.push({ key, label: (fb[key] ?? fa[key])!.label, before: x, after: y, change: x !== null && y !== null ? new D(y).minus(x).toFixed() : null });
  }
  const sa = states(before), sb = states(after);
  const statesOut = [...new Set([...Object.keys(sa), ...Object.keys(sb)])]
    .sort()
    .filter((k) => sa[k]?.state !== sb[k]?.state)
    .map((k) => ({ key: k, label: (sb[k] ?? sa[k])!.label, before: sa[k]?.state ?? "absent", after: sb[k]?.state ?? "absent" }));

  const liveSources = (s: Snapshot) => new Map(s.input.sources.filter((x) => x.deletedAt === null).map((x) => [x.id, x.revision]));
  const ba = liveSources(before), bb = liveSources(after);
  const replacedBefore = new Set(before.analysis.excludedSources.filter((e) => e.reason === "superseded").map((e) => e.sourceId));
  return {
    figures: figuresOut,
    states: statesOut,
    sources: {
      added: [...bb.keys()].filter((id) => !ba.has(id)).sort(),
      removed: [...ba.keys()].filter((id) => !bb.has(id)).sort(),
      revised: [...bb.keys()].filter((id) => ba.has(id) && ba.get(id) !== bb.get(id)).sort(),
    },
    replaced: after.analysis.excludedSources.filter((e) => e.reason === "superseded" && !replacedBefore.has(e.sourceId)).map((e) => e.sourceId),
  };
}
